import { apiClient } from "./ApiClient";

export async function fetchCarriersAdmin({ skip, limit, sort_by, sort_order, filters }) {
  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString(),
    sort_by,
    sort_order,
  });

  for (const key in filters) {
    if (filters[key] !== null && filters[key] !== "") {
      params.append(key, filters[key]);
    }
  }

  try {
    const response = await apiClient.get(`/carrier/batch?${params}`);
    return response.data;
  } catch (error) {
    const errorData = error.response?.data || {};
    throw new Error(errorData.detail || `Failed to fetch carriers: ${error.message}`);
  }
}

export async function fetchCarrierPackages(params = {}) {
  const url = "/admin_packages/batch/internal";
  try {
    const res = await apiClient.get(url, { params });
    return res.data;
  } catch (err) {
    throw new Error(`Fetch carrier packages failed: ${err.message}`);
  }
}

export async function setSuggestedPrice(carrierPackageId) {
  try {
    const res = await apiClient.put(`/admin_packages/${carrierPackageId}/suggested-price`);
    return res.data;
  } catch (err) {
    throw new Error(`Set suggested price failed: ${err.message}`);
  }
}

export async function fetchCarrierAdmin() {
  try {
    const res = await apiClient.get("/admin_packages/carrier");
    return res.data;
  } catch (err) {
    throw new Error(`Fetch admin carrier failed: ${err.message}`);
  }
}

export async function updateCarrierAdmin(body) {
  try {
    const res = await apiClient.put("/admin_packages/carrier", body);
    return res.data;
  } catch (err) {
    throw new Error(`Update admin carrier failed: ${err.message}`);
  }
}

export async function fetchCarrierById(carrierId) {
  try {
    const res = await apiClient.get(`/carrier/${carrierId}`);
    return res.data;
  } catch (err) {
    throw new Error(`Fetch carrier failed: ${err.message}`);
  }
}

export async function updateCarrier(body) {
  try {
    const res = await apiClient.put(`/carrier/`, body);
    return res.data;
  } catch (err) {
    throw new Error(`Update carrier failed: ${err.message}`);
  }
}

export function getPayloadFromDrafts({ price, active, automatic_price, markup_rate, markup_mrg, internal_package_id }) {
  const body = {};
  if (price !== undefined) body.price = Number(price);
  if (active !== undefined) body.active = Boolean(active);
  if (automatic_price !== undefined) body.automatic_price = Boolean(automatic_price);
  if (markup_rate !== undefined) body.markup_rate = Number(markup_rate);
  if (markup_mrg !== undefined) body.markup_mrg = Number(markup_mrg);
  if (internal_package_id !== undefined) body.internal_package_id = internal_package_id;
  return body;
}

// POST /carrier_packages/
export async function postCarrierPackage(body) {
  try {
    const res = await apiClient.post("/admin_packages/", body);
    return res.data;
  } catch (err) {
    throw new Error(`POST carrier_package failed: ${err.message}`);
  }
}

// PUT /carrier_packages/{id}
export async function putCarrierPackage(id, body) {
  try {
    const res = await apiClient.put(`/admin_packages/${id}`, body);
    return res.data;
  } catch (err) {
    throw new Error(`PUT carrier_package failed: ${err.message}`);
  }
}

export async function batchUpdateSelected({ rows, selectedIds, draft, mode = "full" }) {
  const selected = rows.filter((r) => selectedIds.has(r.id));
  const putJobs = [];
  const postJobs = [];

  for (const row of selected) {
    const hasId = Boolean(row.carrier_package_id);
    let body;

    switch (mode) {
      case "activate":
        body = hasId ? buildPutActivatePayload(row, draft) : buildPostActivatePayload(row, draft);
        break;
      case "rate":
        body = hasId ? buildPutRatePayload(row, draft) : buildPostRatePayload(row, draft);
        break;
      case "mrg":
        body = hasId ? buildPutMrgPayload(row, draft) : buildPostMrgPayload(row, draft);
        break;
      case "rateNull":
        body = hasId ? buildPutRateNullPayload(row, draft) : buildPostRateNullPayload(row, draft);
        break;
      case "mrgNull":
        body = hasId ? buildPutMrgNullPayload(row, draft) : buildPostMrgNullPayload(row, draft);
        break;
      case "rateMrgNull":
        body = hasId ? buildPutRateMrgNullPayload(row, draft) : buildPostRateMrgNullPayload(row, draft);
        break;
      default:
        body = hasId ? buildPutPayloadFromRowAndDraft(row, draft) : buildPostPayloadFromRowAndDraft(row, draft);
    }

    if (hasId) putJobs.push(putCarrierPackage(row.carrier_package_id, body));
    else postJobs.push(postCarrierPackage(body));
  }

  const results = await Promise.allSettled([...putJobs, ...postJobs]);
  const errors = results.filter((r) => r.status === "rejected").map((r) => r.reason?.message || String(r.reason));
  return { errors, total: results.length, ok: results.length - errors.length };
}

// src/services/carrierService.js (tam, gdzie masz batchUpdateSelected)
/** Zbuduj pełny payload do POST na podstawie wiersza i tego, co zmieniasz w draft */
function buildPostPayloadFromRowAndDraft(row, draft) {
  const price = draft.price ?? row.price ?? row.carrier_price ?? 0;
  const active = draft.active ?? row.active ?? false;
  const automatic_price = draft.automatic_price ?? row.automatic_price ?? true;
  const markup_rate = draft.markup_rate ?? row.markup_rate ?? 0;
  const markup_mrg = draft.markup_mrg ?? row.markup_mrg ?? 0;

  const internal_package_id = draft.internal_package_id ?? row.internal_package_id ?? row.internalId ?? row.id; // dopasuj do faktycznego pola identyfikującego internal package

  return {
    price: Number(price),
    active: Boolean(active),
    automatic_price: Boolean(automatic_price),
    markup_rate: Number(markup_rate),
    markup_mrg: Number(markup_mrg),
    internal_package_id,
  };
}

/** Zbuduj pełny payload do PUT na podstawie wiersza i draft */
function buildPutPayloadFromRowAndDraft(row, draft) {
  const price = draft.price ?? row.price ?? row.carrier_price ?? 0;
  const active = draft.active ?? row.active ?? false;
  const automatic_price = draft.automatic_price ?? row.automatic_price ?? true;
  const markup_rate = draft.markup_rate ?? row.markup_rate ?? 0;
  const markup_mrg = draft.markup_mrg ?? row.markup_mrg ?? 0;

  return {
    price: Number(price),
    active: Boolean(active),
    automatic_price: Boolean(automatic_price),
    markup_rate: Number(markup_rate),
    markup_mrg: Number(markup_mrg),
  };
}

// POST activate minimal
function buildPostActivatePayload(row, draft) {
  const internal_package_id = row.id;
  const active = draft.active ?? true;

  return {
    active: Boolean(active),
    internal_package_id,
  };
}

// PUT activate minimal
function buildPutActivatePayload(row, draft) {
  const body = { active: Boolean(draft.active ?? true) };
  // Jeśli PUT wymaga price po Waszej stronie, odkomentuj:
  // body.price = Number(draft.price ?? row.price ?? row.carrier_price ?? 0);
  return body;
}

function buildPostRatePayload(row, draft) {
  return {
    internal_package_id: row.id,
    markup_rate: Number(draft.markup_rate),
  };
}
function buildPutRatePayload(row, draft) {
  return { markup_rate: Number(draft.markup_rate) };
}

function buildPostMrgPayload(row, draft) {
  return {
    internal_package_id: row.id,
    markup_mrg: Number(draft.markup_mrg),
  };
}
function buildPutMrgPayload(row, draft) {
  return { markup_mrg: Number(draft.markup_mrg) };
}

// POST null rate
function buildPostRateNullPayload(row, draft) {
  return {
    internal_package_id: row.id,
    markup_rate: null,
  };
}
// PUT null rate
function buildPutRateNullPayload(row, draft) {
  return { markup_rate: null };
}

// POST null mrg
function buildPostMrgNullPayload(row, draft) {
  return {
    internal_package_id: row.id,
    markup_mrg: null,
  };
}
// PUT null mrg
function buildPutMrgNullPayload(row, draft) {
  return { markup_mrg: null };
}

function buildPostRateMrgNullPayload(row, draft) {
  return {
    internal_package_id: row.id,
    markup_rate: null,
    markup_mrg: null,
  };
}
// PUT: oba pola null
function buildPutRateMrgNullPayload(row, draft) {
  return { markup_rate: null, markup_mrg: null };
}

export async function upsertRowPrice(row, token, payload) {
  // payload: { price, automatic_price } LUB { automatic_price: true }
  if (row.carrier_package_id) {
    return putCarrierPackage(row.carrier_package_id, payload, token);
  } else {
    const body = {
      ...payload,
      internal_package_id: row.id,
    };
    return postCarrierPackage(body, token);
  }
}

export async function downloadCarrierPackagesCsv() {
  try {
    const res = await apiClient.get("/admin_packages/csv", {
      responseType: "blob",
    });
    return res.data; // Blob
  } catch (err) {
    throw new Error(`CSV download failed: ${err.message}`);
  }
}
